/**
 * Verifications list page
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { VerificationCard } from '../components/VerificationCard';
import { Loading } from '../components/Loading';
import { verificationApi } from '../services/api';
import type { Verification } from '../../../../packages/shared/src/types';
import type { VerificationDetail } from '../types';

export function Verifications() {
  const navigate = useNavigate();
  const [verifications, setVerifications] = useState<VerificationDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');

  const fetchVerifications = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const response = await verificationApi.list(
      page,
      20,
      undefined,
      statusFilter || undefined
    );

    if (response.success && response.data) {
      setVerifications(response.data.items as any);
      setTotalPages(response.data.totalPages);
      setTotal(response.data.total);
    } else {
      setError(response.error?.message || 'Failed to load verifications');
    }

    setIsLoading(false);
  }, [page, statusFilter]);

  useEffect(() => {
    fetchVerifications();
  }, [fetchVerifications]);

  const handleVerificationClick = useCallback((id: string) => {
    // Find the verification to get the media ID
    const verification = verifications.find(v => v.id === id);
    if (verification) {
      navigate(`/images/${verification.mediaId}`);
    }
  }, [verifications, navigate]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-white">Verifications</h2>
            <p className="text-sm text-gray-400 mt-1">
              {total} verification{total !== 1 ? 's' : ''} total
            </p>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-900/50 border border-red-700 rounded-lg">
            <p className="text-red-300">{error}</p>
          </div>
        )}

        {/* Verifications Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loading size="lg" text="Loading verifications..." />
          </div>
        ) : verifications.length > 0 ? (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {verifications.map((verification) => (
                <VerificationCard
                  key={verification.id}
                  verification={verification as any}
                  onClick={handleVerificationClick}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Previous
                </button>
                <span className="text-gray-400 px-4">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-medium text-white mb-2">No verifications found</h3>
            <p className="text-gray-400">
              {statusFilter ? 'Try changing your filter criteria' : 'Verifications will appear here when users interact with your images'}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Verifications;
